import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { BarChart3, Shield, TrendingUp } from 'lucide-react';

import { DASHBOARD_STACK } from '../../utils/screenNames';

const StatCard = ({ icon, label, value, subtitle, accent = 'text-[#00E7FF]' }) => (
  <div className="bg-app-surface rounded-2xl p-4 md:p-5 flex flex-col gap-2 min-h-[120px]">
    <div className="flex items-center gap-2 text-app-text-secondary text-[13px]">
      <span className={accent}>{icon}</span>
      <span>{label}</span>
    </div>
    <div className="text-app-text text-[22px] font-semibold leading-tight">
      {value}
    </div>
    {subtitle && (
      <div className="text-app-text-secondary text-[12px] truncate">{subtitle}</div>
    )}
  </div>
);

const renderBiggestWin = (data) => {
  if (!data) {
    return {
      value: <span className="text-app-text-secondary">—</span>,
      subtitle: 'No completed matches',
    };
  }
  return {
    value: `${data.homeScore} - ${data.awayScore}`,
    subtitle: `${data.homeTeam?.title || 'TBD'} v ${data.awayTeam?.title || 'TBD'}`,
  };
};

const renderUnbeatenRun = (data) => {
  if (!data) {
    return {
      value: <span className="text-app-text-secondary">—</span>,
      subtitle: 'No completed matches',
    };
  }
  const teamNames = (data.teams || []).map((t) => t.title).filter(Boolean);
  return {
    value: `${data.length} of ${data.outOf}`,
    subtitle: teamNames.length > 0 ? teamNames.join(' · ') : null,
  };
};

const renderAverageGoals = (data) => {
  if (!data || data.totalMatches === 0) {
    return {
      value: <span className="text-app-text-secondary">—</span>,
      subtitle: 'No completed matches',
    };
  }
  return {
    value: data.value.toString(),
    subtitle: `${data.totalGoals} goals · ${data.totalMatches} matches`,
  };
};

const DashboardStatistics = () => {
  const navigate = useNavigate();
  const { selectedTournament, dashboard } = useSelector((state) => state.tournament);
  const tournamentId = selectedTournament?.id || selectedTournament?._id;
  const isFresh = dashboard && dashboard.tournamentId === tournamentId;
  const stats = isFresh ? dashboard.statistics : null;

  const biggestWin = renderBiggestWin(stats?.biggestWin);
  const unbeatenRun = renderUnbeatenRun(stats?.bestUnbeatenRun);
  const averageGoals = renderAverageGoals(stats?.averageGoalsPerGame);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <h3 className="text-app-text text-[18px] font-medium m-0">Tournament statistics</h3>
        <button
          type="button"
          onClick={() => navigate(DASHBOARD_STACK.STATISTICS)}
          className="text-app-text-secondary text-[13px] underline hover:text-app-text transition-colors cursor-pointer"
        >
          See more
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        <StatCard
          icon={<BarChart3 size={16} />}
          label="Biggest win"
          value={biggestWin.value}
          subtitle={biggestWin.subtitle}
        />
        <StatCard
          icon={<Shield size={16} />}
          label="Best unbeaten run"
          value={unbeatenRun.value}
          subtitle={unbeatenRun.subtitle}
        />
        <StatCard
          icon={<TrendingUp size={16} />}
          label="Average goals per game"
          value={averageGoals.value}
          subtitle={averageGoals.subtitle}
        />
      </div>
    </div>
  );
};

export default DashboardStatistics;
